import { IterUtils } from "../../../../../Common/IterableUtils";
import {
    AbsoluteModelPath,
    convertToLegacyPath,
    isModelPath,
    PathToken,
    PathTokens,
} from "../../../../../Common/ModelPath/ModelPath";
import { isNullOrEmpty, reject } from "../../../../../Common/TypingUtils";
import { BindingPath } from "../CoreTypes";

import { PathUtils } from "./PathUtils";

interface MasksTreeNode {
    multiple?: string[];
    names?: { [path: string]: boolean };
    nodes: { [path: string]: undefined | MasksTreeNode };
}

export class MaskRegistrator {
    private readonly masks: MasksTreeNode = { nodes: {} };
    private readonly cache: Map<string, string[]> = new Map();

    public registerMask(nodePath: BindingPath, valueName?: string): void {
        const tokens = PathUtils.split(nodePath);

        let currentNode = this.masks;
        for (const [i, token] of IterUtils.iterateEntries(tokens)) {
            const nodes = currentNode.nodes;
            const tokenNode = nodes[token] || { nodes: {} };

            if (i === tokens.length - 1) {
                if (!tokenNode.names) {
                    tokenNode.names = {};
                }
                tokenNode.names[valueName ?? ""] = true;
            }
            if (token[0] === "*") {
                const multiple = (currentNode.multiple = currentNode.multiple || []);

                if (!multiple.includes(token)) {
                    multiple.push(token);
                }
            }
            nodes[token] = tokenNode;
            currentNode = tokenNode;
        }
        this.cache.clear();
    }

    public unregisterMask(mask: string): void {
        const [nodePath, valueName] = PathUtils.splitToPathAndValueName(mask);
        const tokens = PathUtils.split(nodePath);

        this.cache.clear();
        const nodes: MasksTreeNode[] = [];
        let node: MasksTreeNode = this.masks;

        for (const token of tokens) {
            const nextNode = node.nodes[token];
            if (nextNode == undefined) {
                return;
            }
            nodes.push(node);
            node = nextNode;
        }
        if (!node.names || !node.names[valueName]) {
            return;
        }
        delete node.names[valueName];
        nodes.push(node);
        for (let i = nodes.length - 1; i >= 1; --i) {
            const currNode = nodes[i] ?? reject();

            if (!Object.keys(currNode.nodes || {}).length && !Object.keys(currNode.names || {}).length) {
                const token = tokens[i - 1];
                if (token != undefined) {
                    delete nodes[i - 1]?.nodes[token];
                }
            } else {
                break;
            }
        }
    }

    public getMasks(path: AbsoluteModelPath | BindingPath, valueName?: string): BindingPath[] {
        let result: string[] = [];

        const cacheKey = convertToLegacyPath(path) + (isNullOrEmpty(valueName) ? "" : "." + valueName);
        const cacheValue = this.cache.get(cacheKey);
        if (cacheValue != undefined) {
            result = [...cacheValue];
        } else {
            let tokens: readonly PathToken[];
            if (isModelPath(path)) {
                if (path.isEmpty()) {
                    tokens = [""];
                } else {
                    tokens = path.getPathPartsAsArray();
                }
            } else {
                tokens = PathUtils.split(path);
            }
            const stack: Array<[MasksTreeNode, string, number]> = [[this.masks, "", 0]];

            while (stack.length) {
                const t = stack.pop();
                if (t == undefined) {
                    throw new Error("InvalidProgramStateError");
                }
                const node = t[0];

                if (!node) {
                    continue;
                }
                let accum = t[1];
                const pos = t[2];

                if (pos === tokens.length) {
                    if (node.names && node.names[valueName ?? ""]) {
                        result.push(accum);
                    }
                    continue;
                }
                if (accum !== "") {
                    accum = accum + "/";
                }
                const token = tokens[pos];

                if (node.multiple && (PathTokens.isMultiToken(token) || PathTokens.isInstanceToken(token))) {
                    for (const multipleToken of node.multiple) {
                        const nextMultipleNode = node.nodes[multipleToken];
                        if (nextMultipleNode == undefined) {
                            throw new Error("InvalidProgramStateError");
                        }
                        stack.push([nextMultipleNode, accum + multipleToken, pos + 1]);
                    }
                }
                if (node.nodes) {
                    if (PathTokens.isSimpleToken(token)) {
                        const nextNode = node.nodes[token];
                        if (nextNode != undefined) {
                            stack.push([nextNode, accum + token, pos + 1]);
                        }
                    }
                }
            }
            this.cache.set(cacheKey, [...result]);
        }

        if (valueName != undefined && valueName !== "") {
            for (let i = 0; i < result.length; ++i) {
                result[i] += "." + valueName;
            }
        }

        return result;
    }
}
